import { useId, useState } from 'react'
import './domain-explainer.css'

// DomainExplainer — "How a domain works", in three beats: entities, edges, ego.
// FlowDiagram's collapsible feel, but INLINE — it sits in the page flow (builder
// header, domain-page section) rather than floating as a popover, so it never
// needs click-away or Escape handling.
//
// Two variants:
//   'inline' — the builder's header block. Open by default the FIRST time a user
//              meets the builder (localStorage-remembered), collapsible after.
//   'mark'   — a collapsed "?" affordance for pages that already explain
//              themselves (DomainPage's "What lives here").

interface Beat {
  glyph: string
  name: string
  line: string
}

// Copy mirrors the vocabulary registry's tone: one plain sentence, em-dash examples.
const BEATS: Beat[] = [
  { glyph: '✳', name: 'Entities', line: 'The things this domain tracks — a company, an episode, a person.' },
  { glyph: '↔', name: 'Edges', line: 'How entities relate to each other — guested_on, supplied_by.' },
  {
    glyph: '☉',
    name: 'Your relations (ego)',
    line: 'How they relate to YOU — reading, rated, tracked. The part that makes the domain yours.',
  },
]

const SEEN_KEY = 'dw-domain-explainer-seen'

export interface DomainExplainerProps {
  variant?: 'inline' | 'mark'
  className?: string
}

export function DomainExplainer({ variant = 'inline', className }: DomainExplainerProps) {
  const panelId = useId()
  const [open, setOpen] = useState(() => {
    if (variant === 'mark') return false
    // Open by default the first time only — then remembered shut until asked.
    try {
      if (window.localStorage.getItem(SEEN_KEY)) return false
      window.localStorage.setItem(SEEN_KEY, '1')
      return true
    } catch {
      return true
    }
  })

  return (
    <div className={['dw-domx', `dw-domx--${variant}`, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="dw-domx__head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dw-domx__mark" aria-hidden="true">?</span>
        <span className="dw-domx__title">How a domain works</span>
        <span className="dw-domx__chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div id={panelId} role="region" aria-label="How a domain works" className="dw-domx__beats">
          {BEATS.map((b) => (
            <div className="dw-domx__beat" key={b.name}>
              <span className="dw-domx__glyph" aria-hidden="true">
                <span className="dw-domx__ink">{b.glyph}</span>
              </span>
              <span className="dw-domx__name">{b.name}</span>
              <span className="dw-domx__line">{b.line}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
